/**
 * Session Manager - Manages OpenClaw agent sessions
 *
 * Each session is identified by a SessionKey and maintains:
 * - Conversation history (transcript)
 * - Token count
 * - State (idle, active, paused, error)
 */

import * as fs from "node:fs/promises";
import * as path from "node:path";
import type { ChannelScope, InboundMessage, Session, SessionKey, SessionState } from "./types/index.js";

/** Session entry in sessions.json */
interface SessionEntry {
	id: string;
	key: SessionKey;
	agentId: string;
	channel: string;
	scope: ChannelScope;
	identifier: string;
	createdAt: string;
	lastActivity: string;
	messageCount: number;
	tokenCount: number;
	state: SessionState;
	transcriptPath: string;
}

/** Sessions index file structure */
interface SessionsIndex {
	version: string;
	sessions: Record<SessionKey, SessionEntry>;
}

/** Session manager options */
export interface SessionManagerOptions {
	/** Session storage directory */
	sessionDir: string;
	/** Daily reset hour (0-23) */
	dailyResetHour?: number;
	/** Idle timeout in minutes */
	idleMinutes?: number;
}

/** Session manager */
export class SessionManager {
	private sessionDir: string;
	private dailyResetHour: number;
	private idleMinutes: number;
	private sessions = new Map<SessionKey, Session>();
	private index: SessionsIndex;
	private indexPath: string;
	private saveTimeout?: NodeJS.Timeout;

	constructor(options: SessionManagerOptions) {
		this.sessionDir = options.sessionDir;
		this.dailyResetHour = options.dailyResetHour ?? 4;
		this.idleMinutes = options.idleMinutes ?? 60;
		this.indexPath = path.join(this.sessionDir, "sessions.json");
		this.index = { version: "1.0", sessions: {} };
	}

	/** Initialize session manager */
	async initialize(): Promise<void> {
		await fs.mkdir(this.sessionDir, { recursive: true });
		await this.loadIndex();
	}

	/** Load sessions index */
	private async loadIndex(): Promise<void> {
		try {
			const content = await fs.readFile(this.indexPath, "utf-8");
			this.index = JSON.parse(content);
		} catch {
			// No index file yet, start fresh
			this.index = { version: "1.0", sessions: {} };
		}
	}

	/** Save sessions index (debounced) */
	private scheduleSave(): void {
		if (this.saveTimeout) {
			clearTimeout(this.saveTimeout);
		}
		this.saveTimeout = setTimeout(() => this.saveIndex(), 1000);
	}

	/** Save sessions index immediately */
	private async saveIndex(): Promise<void> {
		const content = JSON.stringify(this.index, null, 2);
		await fs.writeFile(this.indexPath, content, "utf-8");
	}

	/** Build session key */
	buildSessionKey(agentId: string, channel: string, scope: ChannelScope, identifier: string): SessionKey {
		return `agent:${agentId}:${channel}:${scope}:${identifier}`;
	}

	/** Parse session key */
	parseSessionKey(key: SessionKey): {
		agentId: string;
		channel: string;
		scope: ChannelScope;
		identifier: string;
	} {
		const parts = key.split(":");
		return {
			agentId: parts[1],
			channel: parts[2],
			scope: parts[3] as ChannelScope,
			identifier: parts[4],
		};
	}

	/** Get or create session */
	async getOrCreateSession(
		agentId: string,
		channel: string,
		scope: ChannelScope,
		identifier: string,
	): Promise<Session> {
		const key = this.buildSessionKey(agentId, channel, scope, identifier);

		// Check if already loaded
		if (this.sessions.has(key)) {
			return this.sessions.get(key)!;
		}

		// Check if exists in index
		const entry = this.index.sessions[key];
		if (entry) {
			const session: Session = {
				key,
				id: entry.id,
				agentId: entry.agentId,
				channel: entry.channel,
				scope: entry.scope,
				identifier: entry.identifier,
				createdAt: new Date(entry.createdAt),
				lastActivity: new Date(entry.lastActivity),
				messageCount: entry.messageCount,
				tokenCount: entry.tokenCount,
				state: entry.state,
				transcriptPath: entry.transcriptPath,
			};
			this.sessions.set(key, session);
			return session;
		}

		// Create new session
		return this.createSession(agentId, channel, scope, identifier);
	}

	/** Create new session */
	private async createSession(
		agentId: string,
		channel: string,
		scope: ChannelScope,
		identifier: string,
	): Promise<Session> {
		const key = this.buildSessionKey(agentId, channel, scope, identifier);
		const id = this.generateSessionId();
		const transcriptPath = path.join(this.sessionDir, `${id}.jsonl`);

		const session: Session = {
			key,
			id,
			agentId,
			channel,
			scope,
			identifier,
			createdAt: new Date(),
			lastActivity: new Date(),
			messageCount: 0,
			tokenCount: 0,
			state: "idle",
			transcriptPath,
		};

		// Add to index
		this.index.sessions[key] = {
			id,
			key,
			agentId,
			channel,
			scope,
			identifier,
			createdAt: session.createdAt.toISOString(),
			lastActivity: session.lastActivity.toISOString(),
			messageCount: 0,
			tokenCount: 0,
			state: "idle",
			transcriptPath,
		};

		this.sessions.set(key, session);
		await this.saveIndex();

		return session;
	}

	/** Generate unique session ID */
	private generateSessionId(): string {
		const timestamp = Date.now().toString(36);
		const random = Math.random().toString(36).substring(2, 8);
		return `${timestamp}-${random}`;
	}

	/** Get session by key */
	getSession(key: SessionKey): Session | undefined {
		return this.sessions.get(key);
	}

	/** Get all active sessions */
	getActiveSessions(): Session[] {
		return Array.from(this.sessions.values()).filter((s) => s.state === "active");
	}

	/** Get sessions for agent */
	getSessionsForAgent(agentId: string): Session[] {
		return Array.from(this.sessions.values()).filter((s) => s.agentId === agentId);
	}

	/** Update session activity */
	touchSession(key: SessionKey): void {
		const session = this.sessions.get(key);
		if (session) {
			session.lastActivity = new Date();
			const entry = this.index.sessions[key];
			if (entry) {
				entry.lastActivity = session.lastActivity.toISOString();
			}
			this.scheduleSave();
		}
	}

	/** Update session state */
	setSessionState(key: SessionKey, state: SessionState): void {
		const session = this.sessions.get(key);
		if (session) {
			session.state = state;
			const entry = this.index.sessions[key];
			if (entry) {
				entry.state = state;
			}
			this.scheduleSave();
		}
	}

	/** Increment message count */
	incrementMessageCount(key: SessionKey): void {
		const session = this.sessions.get(key);
		if (session) {
			session.messageCount++;
			const entry = this.index.sessions[key];
			if (entry) {
				entry.messageCount = session.messageCount;
			}
			this.scheduleSave();
		}
	}

	/** Update token count */
	updateTokenCount(key: SessionKey, tokens: number): void {
		const session = this.sessions.get(key);
		if (session) {
			session.tokenCount = tokens;
			const entry = this.index.sessions[key];
			if (entry) {
				entry.tokenCount = tokens;
			}
			this.scheduleSave();
		}
	}

	/** Append message to transcript */
	async appendToTranscript(key: SessionKey, message: TranscriptEntry): Promise<void> {
		const session = this.sessions.get(key);
		if (!session) return;

		const line = JSON.stringify(message) + "\n";
		await fs.appendFile(session.transcriptPath, line, "utf-8");

		this.incrementMessageCount(key);
		this.touchSession(key);
	}

	/** Load transcript for session */
	async loadTranscript(key: SessionKey): Promise<TranscriptEntry[]> {
		const session = this.sessions.get(key);
		if (!session) return [];

		try {
			const content = await fs.readFile(session.transcriptPath, "utf-8");
			return content
				.split("\n")
				.filter((line) => line.trim())
				.map((line) => JSON.parse(line));
		} catch {
			return [];
		}
	}

	/** Reset session (clear transcript, keep metadata) */
	async resetSession(key: SessionKey): Promise<void> {
		const session = this.sessions.get(key);
		if (!session) return;

		// Clear transcript file
		try {
			await fs.writeFile(session.transcriptPath, "", "utf-8");
		} catch {
			// Ignore errors
		}

		// Reset counters
		session.messageCount = 0;
		session.tokenCount = 0;
		session.state = "idle";
		session.lastActivity = new Date();

		const entry = this.index.sessions[key];
		if (entry) {
			entry.messageCount = 0;
			entry.tokenCount = 0;
			entry.state = "idle";
			entry.lastActivity = session.lastActivity.toISOString();
		}

		await this.saveIndex();
	}

	/** Close and remove session */
	async closeSession(key: SessionKey): Promise<void> {
		const session = this.sessions.get(key);
		if (session) {
			// Delete transcript file
			try {
				await fs.unlink(session.transcriptPath);
			} catch {
				// Ignore errors
			}
		}

		this.sessions.delete(key);
		delete this.index.sessions[key];
		await this.saveIndex();
	}

	/** Check if session should be reset (daily reset) */
	shouldDailyReset(session: Session): boolean {
		const now = new Date();
		const lastActivity = new Date(session.lastActivity);

		// Check if we're past the reset hour and last activity was before it
		if (now.getHours() >= this.dailyResetHour) {
			const resetTime = new Date(now);
			resetTime.setHours(this.dailyResetHour, 0, 0, 0);

			if (lastActivity < resetTime) {
				return true;
			}
		}

		return false;
	}

	/** Check if session is idle (timeout) */
	isIdle(session: Session): boolean {
		const now = Date.now();
		const lastActivity = new Date(session.lastActivity).getTime();
		const idleMs = this.idleMinutes * 60 * 1000;

		return now - lastActivity > idleMs;
	}

	/** Cleanup old/idle sessions */
	async cleanup(): Promise<number> {
		const toClose: SessionKey[] = [];

		for (const [key, session] of this.sessions) {
			if (this.shouldDailyReset(session) || this.isIdle(session)) {
				toClose.push(key);
			}
		}

		for (const key of toClose) {
			await this.closeSession(key);
		}

		return toClose.length;
	}

	/** Get session statistics */
	getStats(): {
		total: number;
		active: number;
		idle: number;
		totalMessages: number;
		totalTokens: number;
	} {
		const sessions = Array.from(this.sessions.values());
		return {
			total: sessions.length,
			active: sessions.filter((s) => s.state === "active").length,
			idle: sessions.filter((s) => s.state === "idle").length,
			totalMessages: sessions.reduce((sum, s) => sum + s.messageCount, 0),
			totalTokens: sessions.reduce((sum, s) => sum + s.tokenCount, 0),
		};
	}
}

/** Transcript entry types */
export interface TranscriptEntry {
	id: string;
	parentId?: string;
	timestamp: string;
	type: "user" | "assistant" | "tool_call" | "tool_result" | "system";
	content: unknown;
	metadata?: Record<string, unknown>;
}

/** Build session key helper */
export function buildSessionKey(agentId: string, channel: string, scope: ChannelScope, identifier: string): SessionKey {
	return `agent:${agentId}:${channel}:${scope}:${identifier}`;
}
